import { useEffect, type RefObject } from "react";

/** The one script every widget on the site is bootstrapped by. */
export const TRUSTPILOT_BOOTSTRAP =
  "https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js";

/** The one business unit both published widgets read from. */
export const TRUSTPILOT_BUSINESS_UNIT = "5e4ca2a8aa804e00017cebd7";

/** Score, stars and wordmark on one 24px line, which the hero pills use. */
export const TRUSTPILOT_MICRO_STAR = "5419b732fbfb950b10de65e5";

/** The small wordmark and stars the partner campaign heroes set beside a review site's logo. */
export const TRUSTPILOT_MINI = "53aa8807dec7e10d38f59f32";

/** The taller lockup: the standalone strip on `/rotating-proxy`, and `TrustBox` on sign-up and the country heroes. */
export const TRUSTPILOT_MICRO_COMBO = "5419b6ffb0d04a076446a9af";

/** The review page every widget falls back to a link to, until its bootstrap draws the iframe. */
export const TRUSTPILOT_REVIEWS =
  "https://uk.trustpilot.com/review/webshare.io";
/** The same page on the US site, which the `en-US` strip falls back to, as published. */
export const TRUSTPILOT_REVIEWS_US =
  "https://www.trustpilot.com/review/webshare.io";

/** The carousel of reviews the "Our Customers Love Webshare" band draws. */
const TRUSTPILOT_CAROUSEL = "54ad5defc6454f065c28af8b";

/**
 * The review band the home and location pages close on, read off the
 * published `.trustpilot-widget` element attribute for attribute. The template
 * picks the carousel; the rest are the display options the band was configured
 * with, and changing any of them changes what the carousel shows.
 * `data-style-height` is set from `height` so the two cannot drift.
 */
export const TRUSTPILOT_BAND = {
  heading: "Our Customers Love Webshare",
  lottie: "/marketing/home/love-hearts.json",
  /** The published widget's own height, which it keeps at every width. */
  height: 240,
  href: TRUSTPILOT_REVIEWS,
  widget: {
    "data-locale": "en-GB",
    "data-template-id": TRUSTPILOT_CAROUSEL,
    "data-businessunit-id": TRUSTPILOT_BUSINESS_UNIT,
    "data-style-width": "100%",
    "data-theme": "light",
    "data-stars": "5",
    "data-review-languages": "en",
  },
};

/**
 * Hands a widget element to Trustpilot's bootstrap.
 *
 * Attached rather than rendered as JSX: React lifts an async `src` script into
 * the head on the client but leaves it in place on the server, so a `<script>`
 * in the tree hydrates as a mismatch. Cold, the bootstrap finds the element in
 * its own parse-time scan; warm, a client-side navigation onto the page, that
 * scan is long over and the element has to announce itself.
 */
export function useTrustpilotWidget(host: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const element = host.current;
    if (!element) {
      return;
    }
    if (window.Trustpilot) {
      window.Trustpilot.loadFromElement(element, true);
      return;
    }
    if (document.querySelector(`script[src="${TRUSTPILOT_BOOTSTRAP}"]`)) {
      return;
    }
    const script = document.createElement("script");
    script.async = true;
    script.src = TRUSTPILOT_BOOTSTRAP;
    document.head.append(script);
  }, [host]);
}
